import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Input,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { PriceViewCompact } from "../CommonComponents/PriceViewComponent";
import { useBulkApprovePosMutation } from "slices/workflow/WorkFlowSlice";
import type { ApprovalStep, EligibleRow } from "./types";

type ModalStage = "form" | "submitting" | "done";

const Money = ({ value }: { value: number | null | undefined }) => (
  <div className="d-flex justify-content-end">
    {value != null ? (
      <PriceViewCompact price={Number(value).toFixed(2)} />
    ) : (
      <span className="text-muted">-</span>
    )}
  </div>
);

const StepNode = ({ step }: { step: ApprovalStep }) => {
  const status = (step.status || "").toLowerCase();
  const isApproved = status === "approved" || status === "submitted";
  const isSkipped = status === "skipped approval";
  const isRejected = status === "rejected";
  const highlight = step.is_my_step
    ? "border-primary bg-primary-subtle"
    : step.is_current
      ? "border-info bg-info-subtle"
      : isApproved
        ? "border-success-subtle"
        : "border";

  return (
    <div className={`border ${highlight} rounded p-2`} style={{ minWidth: 150 }}>
      <div className="d-flex align-items-center gap-1 mb-1">
        {isApproved ? (
          <i className="ri-checkbox-circle-fill text-success"></i>
        ) : isRejected ? (
          <i className="ri-close-circle-fill text-danger"></i>
        ) : isSkipped ? (
          <i className="ri-skip-forward-fill text-muted"></i>
        ) : step.is_my_step ? (
          <i className="ri-user-star-fill text-primary"></i>
        ) : step.is_current ? (
          <i className="ri-time-fill text-info"></i>
        ) : (
          <i className="ri-circle-line text-muted"></i>
        )}
        <span className="fw-semibold fs-12">{step.step_name}</span>
      </div>
      {step.roles?.length ? (
        <div className="text-muted fs-11">{step.roles.join(", ")}</div>
      ) : null}
      <div className="mt-1 d-flex gap-1 flex-wrap">
        {step.is_my_step ? (
          <Badge color="primary">My Step</Badge>
        ) : step.is_current ? (
          <Badge color="info">Current</Badge>
        ) : (
          <Badge
            color={
              isApproved
                ? "success"
                : isRejected
                  ? "danger"
                  : isSkipped
                    ? "secondary"
                    : "light"
            }
            className={
              !isApproved && !isRejected && !isSkipped ? "text-muted" : ""
            }
          >
            {step.status}
          </Badge>
        )}
      </div>
      {step.approved_by && (
        <div className="text-muted fs-11 mt-1">by {step.approved_by}</div>
      )}
    </div>
  );
};

const POBulkApprovalReview = () => {
  document.title = "PO Bulk Approval Review | CompliPro";
  const navigate = useNavigate();
  const location = useLocation();

  const rows: EligibleRow[] = useMemo(
    () => (location.state as any)?.rows || [],
    [location.state]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [stage, setStage] = useState<ModalStage>("form");
  const [comments, setComments] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any | null>(null);

  const [bulkApprovePos] = useBulkApprovePosMutation();

  const category = rows[0]?.category_name || "-";
  const combinedTotal = rows.reduce(
    (sum, r) => sum + (Number(r.total_cost) || 0),
    0
  );

  const openModal = () => {
    setStage("form");
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (stage === "submitting") return; // locked while the request is in flight
    setModalOpen(false);
    if (stage === "done") {
      navigate("/po-bulk-approval");
    }
  };

  const handleSubmit = async () => {
    if (!comments.trim()) {
      setError("Approval comments are required.");
      return;
    }
    setError(null);
    setStage("submitting");
    try {
      const res: any = await bulkApprovePos({
        approval_ids: rows.map((r) => r.id),
        user_comments: comments.trim(),
        attachments,
      }).unwrap();
      setResults(res);
      setStage("done");
    } catch (err: any) {
      const msg =
        err?.data?.error || err?.data?.detail || "Bulk approval failed.";
      setError(msg);
      setStage("form");
    }
  };

  const outcomeBadge = (row: EligibleRow) => {
    const skipNote = row.skipped_levels?.length
      ? ` (skips ${row.skipped_levels
          .map((s) => s.step_name || `Level ${s.level}`)
          .join(", ")})`
      : "";
    if (row.outcome?.type === "fully_approved") {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle">
          <i className="ri-check-double-line align-middle me-1"></i>
          Will be fully approved{skipNote}
        </span>
      );
    }
    return (
      <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
        <i className="ri-arrow-right-line align-middle me-1"></i>
        Will move to Level {row.outcome?.next_level}
        {row.outcome?.next_roles?.length
          ? ` (${row.outcome.next_roles.join(", ")})`
          : ""}
        {skipNote}
      </span>
    );
  };

  const resultBadge = (outcome: string) => {
    switch (outcome) {
      case "approved":
        return <Badge color="success">Approved</Badge>;
      case "advanced":
        return <Badge color="warning">Advanced</Badge>;
      case "skipped":
        return <Badge color="secondary">Skipped</Badge>;
      default:
        return <Badge color="danger">Failed</Badge>;
    }
  };

  // No selection state (e.g. page refresh) → back to the selection screen.
  if (!rows.length) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Bulk Approval Review"
            pageTitle="PO Bulk Approval"
          />
          <Card>
            <CardBody className="text-center py-5">
              <i className="ri-file-list-3-line display-4 text-muted opacity-50"></i>
              <h5 className="mt-3 text-muted">No purchase orders selected</h5>
              <Button
                color="primary"
                className="mt-2"
                onClick={() => navigate("/po-bulk-approval")}
              >
                <i className="ri-arrow-left-line align-middle me-1"></i>
                Back to PO Bulk Approval
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Bulk Approval Review"
            pageTitle="PO Bulk Approval"
          />

          {/* Summary strip */}
          <Card>
            <CardBody className="py-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div className="d-flex flex-wrap gap-4 align-items-center">
                  <div>
                    <p className="text-muted mb-1 fs-12 text-uppercase">
                      Purchase Orders
                    </p>
                    <h5 className="mb-0">{rows.length}</h5>
                  </div>
                  <div className="vr"></div>
                  <div>
                    <p className="text-muted mb-1 fs-12 text-uppercase">
                      Category
                    </p>
                    <Badge color="primary" className="fs-12">
                      {category}
                    </Badge>
                  </div>
                  <div className="vr"></div>
                  <div>
                    <p className="text-muted mb-1 fs-12 text-uppercase">
                      Combined Total
                    </p>
                    <h5 className="mb-0 text-success">
                      <PriceViewCompact price={combinedTotal.toFixed(2)} />
                    </h5>
                  </div>
                </div>
                <Button
                  color="light"
                  onClick={() => navigate("/po-bulk-approval")}
                >
                  <i className="ri-arrow-left-line align-middle me-1"></i>
                  Back to selection
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Per-PO detail cards */}
          {rows.map((row) => (
            <Card key={row.id} className="mb-3">
              <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-light">
                <div className="d-flex align-items-center gap-2">
                  <Link
                    to={`/purchase-order-detail/${row.po_number}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-semibold fs-15"
                  >
                    {row.po_number}
                    <i className="ri-external-link-line align-middle ms-1 fs-12"></i>
                  </Link>
                  <Badge color="primary" className="badge-border">
                    {row.category_name || "-"}
                  </Badge>
                </div>
                {outcomeBadge(row)}
              </CardHeader>
              <CardBody>
                <Row className="mb-3">
                  <Col md={3} sm={6}>
                    <p className="text-muted mb-1 fs-12">Project</p>
                    <span className="fw-medium">{row.project_name || "-"}</span>
                  </Col>
                  <Col md={3} sm={6}>
                    <p className="text-muted mb-1 fs-12">Supplier</p>
                    <span className="fw-medium">
                      {row.supplier_name || "-"}
                    </span>
                  </Col>
                  <Col md={3} sm={6}>
                    <p className="text-muted mb-1 fs-12">My Approval Step</p>
                    <span className="fw-medium">
                      {row.my_step?.step_name || "-"}
                    </span>
                    {row.my_step?.roles?.length ? (
                      <div className="text-muted fs-11">
                        {row.my_step.roles.join(", ")}
                      </div>
                    ) : null}
                  </Col>
                  <Col md={3} sm={6}>
                    <p className="text-muted mb-1 fs-12">
                      Total Purchase Order Value
                    </p>
                    <span className="fw-semibold text-success">
                      {row.total_cost != null ? (
                        <PriceViewCompact
                          price={Number(row.total_cost).toFixed(2)}
                        />
                      ) : (
                        "-"
                      )}
                    </span>
                  </Col>
                </Row>

                <p className="text-muted mb-2 fs-12 text-uppercase fw-medium">
                  Approval Workflow
                </p>
                <div className="d-flex flex-wrap gap-2 mb-4 align-items-stretch">
                  {(row.steps || []).map((step, idx) => (
                    <React.Fragment key={step.id}>
                      {idx > 0 && (
                        <div className="d-flex align-items-center text-muted">
                          <i className="ri-arrow-right-s-line fs-18"></i>
                        </div>
                      )}
                      <StepNode step={step} />
                    </React.Fragment>
                  ))}
                </div>

                <p className="text-muted mb-2 fs-12 text-uppercase fw-medium">
                  Project Budget Sections
                </p>
                {row.budget_allocations?.length ? (
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "28%" }}>Section</th>
                          <th className="text-end" style={{ width: "18%" }}>
                            Allocated Budget
                          </th>
                          <th className="text-end" style={{ width: "18%" }}>
                            Used
                          </th>
                          <th className="text-end" style={{ width: "18%" }}>
                            This PO (mapped)
                          </th>
                          <th className="text-end" style={{ width: "18%" }}>
                            Remaining Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.budget_allocations.map((b, idx) => (
                          <tr key={idx}>
                            <td className="fw-medium">
                              {b.section_name || "-"}
                            </td>
                            <td>
                              <Money value={b.allocated_cost} />
                            </td>
                            <td>
                              <Money value={b.actual_cost} />
                            </td>
                            <td>
                              <Money value={b.mapped_cost} />
                            </td>
                            <td
                              className={
                                b.remaining_cost < 0
                                  ? "text-danger fw-semibold"
                                  : "text-success"
                              }
                            >
                              <Money value={b.remaining_cost} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-muted fst-italic mb-0 fs-13">
                    No budget section mapped to this PO.
                  </p>
                )}
              </CardBody>
            </Card>
          ))}

          {/* Spacer so the sticky bar doesn't cover the last card */}
          <div style={{ height: 70 }}></div>
        </Container>
      </div>

      {/* Sticky bottom action bar */}
      <div
        className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg py-3"
        style={{ zIndex: 1030 }}
      >
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="text-muted fs-13">
              <i className="ri-checkbox-multiple-line align-middle me-1"></i>
              {rows.length} purchase order{rows.length !== 1 ? "s" : ""} selected
              • {category}
            </span>
            <div className="d-flex gap-2">
              <Button
                color="light"
                onClick={() => navigate("/po-bulk-approval")}
              >
                Cancel
              </Button>
              <Button color="success" onClick={openModal}>
                <i className="ri-checkbox-multiple-line align-middle me-1"></i>
                Approve {rows.length} Purchase Order
                {rows.length !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Approval modal: form → submitting (locked) → results */}
      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={stage === "submitting" ? undefined : closeModal}
        >
          {stage === "done"
            ? "Bulk Approval Results"
            : `Approve ${rows.length} Purchase Order${rows.length !== 1 ? "s" : ""}`}
        </ModalHeader>
        <ModalBody>
          {stage === "done" ? (
            <>
              {results?.summary && (
                <div className="d-flex gap-3 mb-3 flex-wrap">
                  <Badge color="success" className="fs-12">
                    Approved: {results.summary.approved}
                  </Badge>
                  <Badge color="warning" className="fs-12">
                    Advanced: {results.summary.advanced}
                  </Badge>
                  <Badge color="secondary" className="fs-12">
                    Skipped: {results.summary.skipped}
                  </Badge>
                  <Badge color="danger" className="fs-12">
                    Failed: {results.summary.failed}
                  </Badge>
                </div>
              )}
              <div className="table-responsive">
                <Table size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>PO Number</th>
                      <th>Outcome</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(results?.results || []).map((r: any, idx: number) => (
                      <tr key={idx}>
                        <td className="fw-medium">
                          {r.po_number || r.approval_id || "-"}
                        </td>
                        <td>{resultBadge(r.outcome)}</td>
                        <td className="fs-13">
                          {r.outcome === "advanced" && r.next_level != null
                            ? `Next: Level ${r.next_level}${
                                r.next_roles?.length
                                  ? ` (${r.next_roles.join(", ")})`
                                  : ""
                              }`
                            : r.reason || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <>
              <div className="border rounded p-2 mb-3 bg-light small">
                <div className="d-flex justify-content-between">
                  <span>Purchase orders</span>
                  <span className="fw-semibold">{rows.length}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Category</span>
                  <span className="fw-semibold">{category}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Combined total</span>
                  <span className="fw-semibold">
                    <PriceViewCompact price={combinedTotal.toFixed(2)} />
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <Label className="fw-semibold">
                  Approval Comments <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Shared comment recorded on every approved step"
                  disabled={stage === "submitting"}
                />
              </div>
              <div className="mb-2">
                <Label className="fw-semibold">Attachments (optional)</Label>
                <Input
                  type="file"
                  multiple
                  disabled={stage === "submitting"}
                  onChange={(e) =>
                    setAttachments(Array.from(e.target.files || []))
                  }
                />
                {attachments.length > 0 && (
                  <ul className="mt-2 mb-0 ps-3 small">
                    {attachments.map((f, idx) => (
                      <li
                        key={idx}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span className="text-truncate">{f.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0 ms-2"
                          disabled={stage === "submitting"}
                          onClick={() =>
                            setAttachments((prev) =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {stage === "submitting" && (
                <div className="alert alert-info py-2 d-flex align-items-center mb-0 mt-3">
                  <Spinner size="sm" className="me-2" />
                  Approving {rows.length} purchase order
                  {rows.length !== 1 ? "s" : ""}... please don't close this
                  window.
                </div>
              )}
              {error && stage !== "submitting" && (
                <div className="alert alert-danger py-2 mb-0 mt-3">
                  <i className="ri-error-warning-line me-1"></i>
                  {error}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {stage === "done" ? (
            <Button color="primary" onClick={closeModal}>
              <i className="ri-arrow-left-line align-middle me-1"></i>
              Back to PO Bulk Approval
            </Button>
          ) : (
            <>
              <Button
                color="light"
                onClick={closeModal}
                disabled={stage === "submitting"}
              >
                Cancel
              </Button>
              <Button
                color="success"
                onClick={handleSubmit}
                disabled={stage === "submitting"}
              >
                {stage === "submitting" ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Approving...
                  </>
                ) : (
                  "Confirm Approval"
                )}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default POBulkApprovalReview;
